import { useState, useMemo, useRef } from "react"
import { Autocomplete, TextField, Typography } from "@mui/material"
import { useCloseOnScrollOutOfView } from "hooks/use-close-on-scroll-out-of-view"
import type { FilterOption } from "./index"
import UniverseSearchOption from "./universe-search-option"

interface FilterAutocompleteProps {
  label: string
  options: FilterOption[]
  applyLongToShort?: boolean
  onChange: (newValue: FilterOption | null) => void
  overrideValue?: FilterOption | null
  value: FilterOption | null
  onMenuOpen?: () => void
}

export default function FilterAutocomplete({
  applyLongToShort = false,
  label,
  options,
  onChange,
  overrideValue,
  value,
  onMenuOpen,
}: FilterAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  function handleOpen() {
    onMenuOpen?.()
    setOpen(true)
  }

  // Force a certain direction until options are loaded so the menu doesn't jump around
  const popperProps = useMemo(() => {
    if (options.length > 1) return undefined
    return {
      modifiers: [
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
      ],
    }
  }, [options.length])

  useCloseOnScrollOutOfView(open, setOpen, anchorRef)

  const selectedValue = applyLongToShort ? overrideValue : value

  return (
    <div ref={anchorRef} className="autocomplete-field">
      <Autocomplete
        size="small"
        options={options}
        disabled={applyLongToShort}
        value={selectedValue}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => option.disabled ?? false}
        onOpen={handleOpen}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          popper: popperProps,
        }}
        renderInput={(params) => <TextField
          {...params}
          label={label}
        />}
        renderOption={(props, option) => <UniverseSearchOption option={option} props={props} numOptions={options.length} />}
        groupBy={(option) => option.group ?? ""}
        renderGroup={(params) => (
        <li key={params.key}>
          {params.group && <Typography variant="subtitle2" className="saved-searches-header">{params.group.toUpperCase()}</Typography>}
          <ul className="saved-searches-list">{params.children}</ul>
        </li>
      )}
      />
    </div>
  )
}
